import { useEffect, useMemo, useState } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Spinner } from '@telegram-apps/telegram-ui';
import { searchTitles } from '../../shared/api/client';
import { EmptyState, ErrorState, TitleListItem } from '../../components/ui';
import { MEDIA_TYPE_LABELS } from '@mass-cult/shared';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

export function SearchPage() {
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 350);

  const searchQuery = useInfiniteQuery({
    queryKey: ['search', debouncedQuery],
    enabled: debouncedQuery.trim().length > 0,
    initialPageParam: 1,
    queryFn: ({ pageParam }) => searchTitles(debouncedQuery, pageParam),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.totalPages ? lastPage.page + 1 : undefined,
  });

  const results = useMemo(
    () => searchQuery.data?.pages.flatMap((page) => page.results) ?? [],
    [searchQuery.data],
  );

  return (
    <div>
      <h1>Поиск</h1>
      <input
        className="search-input"
        placeholder="Название фильма или сериала"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      {!debouncedQuery.trim() && (
        <EmptyState
          title="Начните поиск"
          description="Введите название фильма или сериала"
        />
      )}

      {searchQuery.isLoading && debouncedQuery.trim() && <Spinner size="m" />}

      {searchQuery.isError && (
        <ErrorState message={(searchQuery.error as Error).message} />
      )}

      {debouncedQuery.trim() && !searchQuery.isLoading && results.length === 0 && (
        <EmptyState title="Ничего не найдено" />
      )}

      {results.map((item) => (
        <TitleListItem
          key={`${item.mediaType}-${item.tmdbId}`}
          mediaType={item.mediaType}
          tmdbId={item.tmdbId}
          title={item.title}
          subtitle={[
            item.year,
            MEDIA_TYPE_LABELS[item.mediaType],
            item.tmdbRating ? `TMDB ${item.tmdbRating.toFixed(1)}` : null,
          ]
            .filter(Boolean)
            .join(' · ')}
          posterUrl={item.posterUrl}
        />
      ))}

      {searchQuery.hasNextPage && (
        <Button
          stretched
          mode="bezeled"
          onClick={() => searchQuery.fetchNextPage()}
          disabled={searchQuery.isFetchingNextPage}
        >
          {searchQuery.isFetchingNextPage ? 'Загрузка…' : 'Показать ещё'}
        </Button>
      )}
    </div>
  );
}
